import { ApplicationResolution } from '../client/applicationResolution';
import { ElasticAPMRumOptions } from '../server/elasticApmOptions';

export type Avatar = {
  id: string;
  altTexts?: {
    [language: string]: string;
  };
};

export type Mode =
  | 'hbbtv'
  | 'webos'
  | 'tizen'
  | 'philips'
  | 'vestel'
  | 'panasonic'
  | 'telia'
  | 'elisa'
  | 'hisense'
  | 'tivo'
  | 'sony'
  | 'ps4'
  | 'ps5';

export interface YleApiCredentials {
  id: string | undefined;
  key: string | undefined;
}

export type YleApiCredentialsPerMode = {
  [P in Mode]: YleApiCredentials;
};

type Verification = 'sms';

export interface ProfileCookies {
  yletestlogin?: string;
  ylelogin?: string;
}

export interface LoggedInUser {
  age?: number;
  avatar?: string;
  consent: {
    values: {
      development: boolean;
      embedded_social_media: boolean;
      targeting: boolean;
      personalization: boolean;
    };
    version: string;
    expired: boolean;
  };
  displayName?: string;
  initials: string;
  isSubAccount: boolean;
  latestTermsOfServiceAccepted: boolean;
  nick: string | null;
  nickUpdatedAt: string | null;
  parentId?: string;
  profileColor?: string;
  transientId?: string;
  userId: string;
  username: string;
  verifications: Verification[];
}

export interface ErrorObject {
  message: string | Event;
  source: string | undefined;
  lineno: number | undefined;
  colno: number | undefined;
  error: Error | undefined;
}

export type YleLeanback = {
  VERSION: string;
  PUBLIC_PATH_PREFIX: string;
  RESOLUTION: ApplicationResolution;
  E2E_TESTS: boolean;
  MODE: Mode;
  elasticAPMOptions: ElasticAPMRumOptions;
  clientYleApiCredentials: YleApiCredentials;
  MUX_ENV_KEY: string;
  BRAZE_API_KEY: string;
  errorStack?: Array<ErrorObject>;
  originalWindowOnError?: OnErrorEventHandler;
  FEATURE_BRAZE: boolean;
};

export type ProfileIcon = {
  type: 'color' | 'image';
  value: string;
};

export type ProfileIconColor = ProfileIcon & {
  type: 'color';
};

export type ProfileIconImage = ProfileIcon & {
  type: 'image';
};

export type LoggedInSubAccount = Omit<
  LoggedInUser,
  'nick' | 'nickUpdatedAt' | 'username'
> & {
  parentId: string;
  displayName: string;
  nick: null;
  nickUpdatedAt: null;
  avatar?: string;
};

export type Profile = {
  age: number;
  consent: LoggedInUser['consent']; // although not visible, this is used in 5xx login API-error recovery
  cookie: string | undefined;
  displayName: string | undefined;
  icon: ProfileIconColor | ProfileIconImage;
  initials: string;
  nick: string | undefined;
  parentId: string | null;
  userId: string;
  username?: string;
};

export type SubProfile = Omit<Profile, 'parentId' | 'nick' | 'username'> & {
  parentId: string;
  nick: undefined;
};

export type ParentProfile = Omit<
  Profile,
  'parentId' | 'nick' | 'icon' | 'cookie'
> & {
  icon: ProfileIconColor;
  parentId: null;
  nick: string;
  cookie: string;
  username: string;
};

export const hbbTvMode = 'hbbtv';
export const webOsMode = 'webos';
export const tizenMode = 'tizen';
export const philipsMode = 'philips';
export const vestelMode = 'vestel';
export const panasonicMode = 'panasonic';
export const teliaMode = 'telia';
export const elisaMode = 'elisa';
export const hisenseMode = 'hisense';
export const tivoMode = 'tivo';
export const sonyMode = 'sony';
export const ps4Mode = 'ps4';
export const ps5Mode = 'ps5';

export type AreenaApiClient = 'yle-areena-leanback';
